import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import '../../App.css';
import CSS from 'csstype';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// icons
import { BellRing, Settings, Square, SquareCheckBig, SunMoon } from 'lucide-react';

// types
import { Site, Theme, UserType } from '../../utils/types';

// types relating to events.
import { Event, EventType, EventTarget, GatewaySettingsType } from '../../utils/events';

// functions/
import { siteName } from '../../utils/functions';

// classes
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import Tool from '../../tools/tool';
import Scrollbox from '../../tools/scrollbox';
import { getFocusableElements } from '../../utils/accessibility';
import { CheckBox } from '../../tools/controls';
import { handleEvent } from '../../app-actions';

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

import { setRenamingFile } from './project-slice';
import { getUserProjectFiles } from './project-actions';
import { renameFile as fb_renameFile } from '../data-management/file-browser-actions';

//  --------------------------------------------------------------------------
//
//  T Y P E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  P R O P E R T I E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  H T M L   C U S T O M   C O M P O N E N T S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  C L A S S   D E F I N I T I O N
//
//  --------------------------------------------------------------------------

export default function RenameFile(props: { onClose: () => void; filename: string }) {
  //
  //  --------------------------------------------------------------------------
  //
  //  C O N S T A N T S
  //
  //  --------------------------------------------------------------------------

  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  const [sFilename, setFilename] = useState<string>(props.filename);

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Handler for changes to the input boxes.
  //
  //	------------------------------------------------------------

  const inputHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.target;

    // update the current filename state.
    if (inputBox.name === 'rename-filename') {
      setFilename(inputBox.value);
    }
    //
  }; // inputHandler

  //  ------------------------------------------------------------
  //
  //  Handler for clicks to buttons
  //
  //  ------------------------------------------------------------

  const onClickHandler = (event: React.MouseEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.currentTarget;

    if (inputBox.name === 'close-dialog') {
      props.onClose();
    }
    //
  }; // onClickHandler

  //  ------------------------------------------------------------
  //
  //  Rename the file.
  //
  //  ------------------------------------------------------------

  function renameFile() {
    //
    // rename the file.
    dispatch(
      fb_renameFile({
        oldFilename: props.filename,
        newFilename: sFilename,
        storageType: 'user',
        storagePath: [],
        setLoadingFlag: setRenamingFile,
        runAfterwards: getUserProjectFiles,
      }),
    );

    // close the dialog.
    props.onClose();
    //
  } // renameFile

  //  --------------------------------------------------------------------------
  //
  //  H O O K S
  //
  //  --------------------------------------------------------------------------

  //	-------------------------------------------------
  //
  //	Handle the closing of the dialog.
  //
  //	-------------------------------------------------

  useEffect(() => {
    //
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        props.onClose();
      }
      if (event.key === 'Enter') {
        renameFile();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
    //
  }, [props.onClose]);

  //  --------------------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  --------------------------------------------------------------------------

  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    //
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const focusFirstElement = () => {
      const focusable = getFocusableElements(dialog);
      const target = focusable[0] ?? dialog;
      window.requestAnimationFrame(() => {
        target.focus();
      });
    };
    //
  }, []);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rename-file-title"
      tabIndex={-1}
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <input
        aria-labelledby="rename-file-title"
        className="input-field"
        type="text"
        name="rename-filename"
        onChange={inputHandler}
        maxLength={255}
        placeholder={t('Filename')}
        value={sFilename}
      />
      <div
        style={{
          flex: '0 0 60px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <ToolButton
          key={t('Close')}
          name="close-dialog"
          onClick={onClickHandler}
          text={t('Close')}
          type={ToolButtonType.SECONDARY}
        />
        <div style={{ flex: '1 1' }} />
        <ToolButton
          key={t('Rename')}
          name="rename-file"
          onClick={() => renameFile()}
          text={t('Rename')}
          type={ToolButtonType.SECONDARY}
        />
        <div style={{ flex: '0 0 15px' }} />
      </div>
    </div>
  );
} // RenameFile
